/**
 * Realtor listing detail (owner view).
 *
 * Owner-only page. Renders the listing fields plus lifecycle action
 * buttons:
 *   - Publish (verified-realtor only) / Unpublish
 *   - Mark as sold
 *   - Edit (routes to capture page)
 *   - Delete (drafts only — sold listings can't be deleted)
 *
 * The page reads the realtor profile's verification flags to decide
 * whether to enable the publish CTA. Server-side enforcement is the
 * source of truth (route + service); the client gate just hides a
 * button that would 403.
 */

import {
	AlertCircle,
	ArrowLeft,
	CheckCircle2,
	Eye,
	EyeOff,
	KeyRound,
	Pencil,
	Trash2,
} from "lucide-react";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { BuyerHandoffSection } from "@/features/realtor/components/buyer-handoff-section";
import { InterestedBuyersSection } from "@/features/realtor/components/interested-buyers-section";
import { ListingStatusBadge } from "@/features/realtor/components/listing-card";
import { ShareListingDialog } from "@/features/realtor/components/share-listing-dialog";
import { useListingLinks } from "@/features/realtor/hooks/use-listing-links";
import {
	type LeadLinkedListing,
	type LeadWithLinks,
	listMyLeads,
} from "@/features/realtor/services/leads-api.service";
import {
	deleteListing,
	getMyListing,
	type Listing,
	markListingSold,
	publishListing,
	unpublishListing,
} from "@/features/realtor/services/listings-api.service";
import {
	getMyRealtorProfile,
	type RealtorProfile,
} from "@/features/realtor/services/realtor-api.service";
import { formatPaiseAsRupees } from "@/features/realtor/utils/listings-format";
import { toShareableListing } from "@/features/realtor/utils/share-listing";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| {
			status: "loaded";
			listing: Listing;
			realtor: RealtorProfile | null;
			// Leads soft-degrade independently of the listing: a leads-fetch
			// failure sets `leadsError` but still renders the page.
			leads: LeadWithLinks[];
			leadsError: boolean;
	  };

export function ListingDetailPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [confirmMarkSold, setConfirmMarkSold] = useState(false);
	// An action or buyer link can settle after the realtor has left the
	// listing it was started on: they navigated away (the page unmounts) or
	// the route moved to another listing (the page stays mounted). Each
	// handler records the listing it acted on and applies a success (state,
	// toast, navigation) only while that listing is still the one shown; a
	// failure is reported wherever the realtor is, since it is still true of
	// the listing they acted on (see reportFailure). The busy flags hold
	// every listing with a request in flight, so one listing's request can
	// neither mark another busy nor clear another's busy state.
	const [actionsInFlight, setActionsInFlight] = useState<ReadonlySet<string>>(
		() => new Set(),
	);
	const [linksInFlight, setLinksInFlight] = useState<ReadonlySet<string>>(
		() => new Set(),
	);
	const shownIdRef = useRef<string | undefined>(id);
	useLayoutEffect(() => {
		shownIdRef.current = id;
		return () => {
			shownIdRef.current = undefined;
		};
	}, [id]);
	const isStillShown = (listingId: string): boolean =>
		shownIdRef.current === listingId;
	// Off the listing it is about, the error toast names that listing, so it
	// does not read as a failure of the listing now on screen.
	const reportFailure = (
		message: string,
		actedOn: Pick<Listing, "id" | "headline">,
	): void => {
		if (isStillShown(actedOn.id)) {
			toast.error(message);
		} else {
			toast.error(message, { description: actedOn.headline });
		}
	};
	const withListing =
		(listingId: string) =>
		(prev: ReadonlySet<string>): ReadonlySet<string> =>
			new Set(prev).add(listingId);
	const withoutListing =
		(listingId: string) =>
		(prev: ReadonlySet<string>): ReadonlySet<string> => {
			if (!prev.has(listingId)) return prev;
			const next = new Set(prev);
			next.delete(listingId);
			return next;
		};
	const { linkOne, unlinkOne } = useListingLinks();

	const loadAll = useCallback(
		async (cancelledRef: { cancelled: boolean }) => {
			if (!id) {
				if (!cancelledRef.cancelled) {
					setLoad({
						status: "error",
						message: t("realtor.listings.detail.missingId"),
					});
				}
				return;
			}
			setLoad({ status: "loading" });
			// Three parallel loads with THREE different failure policies:
			//   - listing  → HARD page error (can't render without it)
			//   - profile  → SOFT (fall back to null; page still renders)
			//   - leads    → SOFT (fall back to leadsError; the Interested-
			//                buyers section shows an inline retry, but
			//                publish/delete/mark-sold keep working)
			const [listingResult, profileResult, leadsResult] = await Promise.all([
				getMyListing(id),
				getMyRealtorProfile(),
				listMyLeads(),
			]);
			if (cancelledRef.cancelled) return;
			if (!listingResult.success || !listingResult.data) {
				setLoad({
					status: "error",
					message:
						listingResult.error ?? t("realtor.listings.loadFailedFallback"),
				});
				return;
			}
			setLoad({
				status: "loaded",
				listing: listingResult.data,
				realtor: profileResult.success ? (profileResult.profile ?? null) : null,
				leads: leadsResult.success && leadsResult.data ? leadsResult.data : [],
				leadsError: !leadsResult.success,
			});
		},
		[id, t],
	);

	useEffect(() => {
		const ref = { cancelled: false };
		loadAll(ref);
		return () => {
			ref.cancelled = true;
		};
	}, [loadAll]);

	// Re-fetch just the leads. Two entry points:
	//   - the inline retry inside the Interested-buyers section (surfaces a
	//     failure by flipping leadsError back on);
	//   - post-write reconcile after a successful link/unlink — because
	//     link/unlink return no server echo, we re-fetch to replace the
	//     optimistic row with the authoritative one. A reconcile-refetch
	//     that itself fails keeps the (already-committed) optimistic state
	//     rather than falsely flagging an error.
	const refetchLeads = useCallback(async (surfaceError: boolean) => {
		const result = await listMyLeads();
		setLoad((prev) => {
			if (prev.status !== "loaded") return prev;
			if (!result.success || !result.data) {
				return surfaceError ? { ...prev, leadsError: true } : prev;
			}
			return { ...prev, leads: result.data, leadsError: false };
		});
	}, []);

	// Optimistically add `leadId` to this listing's interested buyers, then
	// link + reconcile-by-refetch; roll back on failure.
	const handleAddBuyer = async (leadId: string) => {
		if (load.status !== "loaded") return;
		const { listing } = load;
		const snapshot: LeadLinkedListing = {
			id: listing.id,
			headline: listing.headline,
			propertyType: listing.propertyType,
			bhk: listing.bhk,
			locality: listing.locality,
			city: listing.city,
		};
		setLinksInFlight(withListing(listing.id));
		try {
			setLoad((prev) =>
				prev.status === "loaded"
					? {
							...prev,
							leads: prev.leads.map((e) =>
								e.lead.id === leadId
									? { ...e, listings: [...e.listings, snapshot] }
									: e,
							),
						}
					: prev,
			);
			const result = await linkOne(leadId, listing.id);
			if (!result.success) {
				// Roll back the optimistic add, on whichever listing is shown:
				// it only drops the failed link from the lead, and the server
				// does not have that link either.
				setLoad((prev) =>
					prev.status === "loaded"
						? {
								...prev,
								leads: prev.leads.map((e) =>
									e.lead.id === leadId
										? {
												...e,
												listings: e.listings.filter((l) => l.id !== listing.id),
											}
										: e,
								),
							}
						: prev,
				);
				reportFailure(
					result.error ?? t("realtor.listings.detail.linkFailFallback"),
					listing,
				);
				return;
			}
			// Moved on: whichever listing is shown, if any, loads its own leads,
			// and this link changes nothing it displays.
			if (!isStillShown(listing.id)) return;
			await refetchLeads(false);
		} finally {
			// Guard the in-flight flag against an unexpected throw so the
			// picker/unlink can't stay disabled for the page lifetime.
			setLinksInFlight(withoutListing(listing.id));
		}
	};

	// Optimistically remove `leadId` from this listing's interested buyers,
	// then unlink + reconcile-by-refetch; roll back on failure.
	const handleUnlinkBuyer = async (leadId: string) => {
		if (load.status !== "loaded") return;
		const { listing } = load;
		const previous = load.leads.find((e) => e.lead.id === leadId);
		if (!previous) return;
		setLinksInFlight(withListing(listing.id));
		try {
			setLoad((prev) =>
				prev.status === "loaded"
					? {
							...prev,
							leads: prev.leads.map((e) =>
								e.lead.id === leadId
									? {
											...e,
											listings: e.listings.filter((l) => l.id !== listing.id),
										}
									: e,
							),
						}
					: prev,
			);
			const result = await unlinkOne(leadId, listing.id);
			const stillShown = isStillShown(listing.id);
			if (!result.success) {
				// Roll back to the pre-unlink listings, only on this listing:
				// they are the lead's links as this listing loaded them, and on
				// another listing they would overwrite what it loaded.
				if (stillShown) {
					setLoad((prev) =>
						prev.status === "loaded"
							? {
									...prev,
									leads: prev.leads.map((e) =>
										e.lead.id === leadId
											? { ...e, listings: previous.listings }
											: e,
									),
								}
							: prev,
					);
				}
				reportFailure(
					result.error ?? t("realtor.listings.detail.linkFailFallback"),
					listing,
				);
				return;
			}
			if (!stillShown) return;
			await refetchLeads(false);
		} finally {
			// Guard the in-flight flag against an unexpected throw so the
			// picker/unlink can't stay disabled for the page lifetime.
			setLinksInFlight(withoutListing(listing.id));
		}
	};
	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-8"
				data-testid="listing-detail-loading"
			>
				<Skeleton className="h-40 w-full bg-dashboard-bg" />
				<Skeleton className="h-64 w-full bg-dashboard-bg" />
				<Skeleton className="h-32 w-full bg-dashboard-bg" />
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<p className="text-sm text-dashboard-muted">{load.message}</p>
						<StudioButton onClick={() => navigate("/realtor/listings")}>
							{t("realtor.listings.backToList")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { listing, realtor, leads, leadsError } = load;
	const actionInFlight = actionsInFlight.has(listing.id);
	const linkInFlight = linksInFlight.has(listing.id);
	const interestedLeads = leads.filter((e) =>
		e.listings.some((l) => l.id === listing.id),
	);
	const eligibleLeads = leads.filter(
		(e) => !e.listings.some((l) => l.id === listing.id),
	);
	const isVerified = Boolean(realtor?.reraVerified && realtor?.idVerified);
	const canPublish =
		isVerified &&
		!listing.isPublic &&
		listing.status !== "sold" &&
		listing.status !== "withdrawn";
	const canUnpublish = listing.isPublic && listing.status !== "withdrawn";
	const canMarkSold =
		listing.status === "active" || listing.status === "under_negotiation";
	const canDelete = listing.status !== "sold";

	const runAction = async (
		fn: () => Promise<{ success: boolean; data?: Listing; error?: string }>,
		successMessage: string,
		fallbackMessage: string,
	) => {
		const actedOn = listing.id;
		setActionsInFlight(withListing(actedOn));
		try {
			const result = await fn();
			// A failure is reported wherever the realtor is: it is true of the
			// listing they acted on. Success is shown only on that listing.
			if (!result.success || !result.data) {
				reportFailure(result.error ?? fallbackMessage, listing);
				return;
			}
			if (!isStillShown(actedOn)) return;
			toast.success(successMessage);
			const updated = result.data;
			setLoad((prev) =>
				prev.status === "loaded"
					? { ...prev, listing: updated }
					: { status: "loaded", listing: updated, realtor, leads, leadsError },
			);
		} catch (error) {
			console.error("Listing action failed unexpectedly", error);
			reportFailure(fallbackMessage, listing);
		} finally {
			setActionsInFlight(withoutListing(actedOn));
		}
	};
	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-8">
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6">
					<button
						type="button"
						onClick={() => navigate("/realtor/listings")}
						className="flex items-center gap-2 text-dashboard-muted hover:text-dashboard-text transition-colors mb-4"
					>
						<ArrowLeft className="h-4 w-4" />
						<span className="text-sm font-body">Back to listings</span>
					</button>

					<p className="text-xs font-mono uppercase tracking-widest text-dashboard-muted">
						Property Details
					</p>
					<h1
						className="font-display text-3xl italic font-medium text-dashboard-text mt-2 truncate"
						style={{ lineHeight: 1.1 }}
					>
						{listing.headline}
					</h1>
					<p className="text-sm text-dashboard-muted mt-2 truncate">
						{listing.locality} · {listing.city}
					</p>

					<div className="mt-4">
						<ListingStatusBadge
							status={listing.status}
							isPublic={listing.isPublic}
						/>
					</div>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-4">
					<div className="grid grid-cols-2 gap-4">
						<KeyValue
							label={t("realtor.listings.detail.price")}
							value={formatPaiseAsRupees(listing.askingPrice)}
						/>
						<KeyValue
							label={t("realtor.listings.detail.propertyType")}
							value={t(`realtor.listings.propertyType.${listing.propertyType}`)}
						/>
						{listing.bhk != null && (
							<KeyValue
								label={t("realtor.listings.detail.bhk")}
								value={listing.bhk.toString()}
							/>
						)}
						{listing.sizeSqft != null && (
							<KeyValue
								label={t("realtor.listings.detail.size")}
								value={`${listing.sizeSqft} ${t("realtor.listings.sqft")}`}
							/>
						)}
					</div>
					<div
						style={{
							borderTop: "1px solid var(--color-dashboard-border)",
							paddingTop: 12,
						}}
						className="space-y-2"
					>
						<KeyValue
							label={t("realtor.listings.detail.addressPublic")}
							value={listing.addressPublic}
						/>
						<KeyValue
							label={t("realtor.listings.detail.addressPrivate")}
							value={listing.addressPrivate}
							isPrivate
						/>
					</div>
					{listing.description && (
						<div
							style={{
								borderTop: "1px solid var(--color-dashboard-border)",
								paddingTop: 12,
							}}
						>
							<p className="text-xs font-mono uppercase tracking-wider text-dashboard-muted mb-2">
								{t("realtor.listings.detail.description")}
							</p>
							<p className="text-sm text-dashboard-text whitespace-pre-line">
								{listing.description}
							</p>
						</div>
					)}
					{listing.amenities.length > 0 && (
						<div
							style={{
								borderTop: "1px solid var(--color-dashboard-border)",
								paddingTop: 12,
							}}
						>
							<p className="text-xs font-mono uppercase tracking-wider text-dashboard-muted mb-2">
								{t("realtor.listings.detail.amenities")}
							</p>
							<div className="flex flex-wrap gap-2">
								{listing.amenities.map((a) => (
									<span
										key={a}
										className="inline-flex items-center rounded-full px-3 py-0.5 text-xs"
										style={{
											backgroundColor: "var(--color-dashboard-border)",
											color: "var(--color-dashboard-muted)",
										}}
									>
										{a}
									</span>
								))}
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			{!isVerified &&
				!listing.isPublic &&
				listing.status !== "sold" &&
				listing.status !== "withdrawn" && (
					<Card
						style={{
							borderColor: "var(--color-dashboard-pill-awaiting-bg)",
							backgroundColor: "var(--color-dashboard-pill-awaiting-bg)",
						}}
					>
						<CardContent className="p-4 flex items-start gap-3">
							<AlertCircle
								className="h-4 w-4 shrink-0 mt-0.5"
								style={{ color: "var(--color-dashboard-pill-awaiting)" }}
							/>
							<div className="space-y-2">
								<p className="text-xs text-dashboard-text">
									{t("realtor.listings.detail.verificationGate")}
								</p>
								<StudioButton
									size="sm"
									onClick={() => navigate("/realtor/verification")}
								>
									{t("realtor.listings.detail.startVerificationCta")}
								</StudioButton>
							</div>
						</CardContent>
					</Card>
				)}

			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-4 flex flex-wrap gap-2">
					{listing.isPublic && listing.publicSlug && (
						<ShareListingDialog
							listing={toShareableListing(listing)}
							triggerLabel={t("realtor.listings.share.trigger")}
						/>
					)}
					<StudioButton
						variant="secondary"
						size="sm"
						onClick={() => navigate(`/realtor/listings/${listing.id}/edit`)}
					>
						<Pencil className="h-4 w-4" />
						{t("realtor.listings.detail.edit")}
					</StudioButton>
					{canPublish && (
						<StudioButton
							size="sm"
							className="gap-1.5"
							disabled={actionInFlight}
							onClick={() =>
								runAction(
									() => publishListing(listing.id),
									t("realtor.listings.detail.publishedToast"),
									t("realtor.listings.detail.publishFailFallback"),
								)
							}
						>
							<Eye className="h-4 w-4" />
							{t("realtor.listings.detail.publish")}
						</StudioButton>
					)}
					{canUnpublish && (
						<StudioButton
							variant="secondary"
							size="sm"
							disabled={actionInFlight}
							onClick={() =>
								runAction(
									() => unpublishListing(listing.id),
									t("realtor.listings.detail.unpublishedToast"),
									t("realtor.listings.detail.unpublishFailFallback"),
								)
							}
						>
							<EyeOff className="h-4 w-4" />
							{t("realtor.listings.detail.unpublish")}
						</StudioButton>
					)}
					{canMarkSold && (
						<StudioButton
							variant="secondary"
							size="sm"
							disabled={actionInFlight}
							onClick={() => setConfirmMarkSold(true)}
						>
							<KeyRound className="h-4 w-4" />
							{t("realtor.listings.detail.markSold")}
						</StudioButton>
					)}
					{canDelete && (
						<StudioButton
							variant="secondary"
							size="sm"
							style={{ color: "var(--color-dashboard-pill-risk)" }}
							disabled={actionInFlight}
							onClick={() => setConfirmDelete(true)}
						>
							<Trash2 className="h-4 w-4" />
							{t("realtor.listings.detail.delete")}
						</StudioButton>
					)}
				</CardContent>
			</Card>

			<InterestedBuyersSection
				interested={interestedLeads}
				eligible={eligibleLeads}
				leadsError={leadsError}
				busy={linkInFlight}
				onRetry={() => refetchLeads(true)}
				onAdd={handleAddBuyer}
				onUnlink={handleUnlinkBuyer}
			/>

			{listing.status === "sold" && (
				<>
					<Card
						style={{
							borderColor: "var(--color-dashboard-pill-done)",
							backgroundColor: "var(--color-dashboard-pill-done-bg)",
						}}
					>
						<CardContent className="p-4 flex items-start gap-3">
							<CheckCircle2
								className="h-4 w-4 shrink-0 mt-0.5"
								style={{ color: "var(--color-dashboard-pill-done)" }}
							/>
							<p
								className="text-xs"
								style={{ color: "var(--color-dashboard-pill-done)" }}
							>
								{t("realtor.listings.detail.soldNotice", {
									date: listing.soldAt
										? new Date(listing.soldAt).toLocaleDateString()
										: "",
								})}
							</p>
						</CardContent>
					</Card>
					<BuyerHandoffSection listingId={listing.id} />
				</>
			)}

			<Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
				<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<DialogHeader>
						<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
							{t("realtor.listings.detail.deleteConfirmTitle")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.listings.detail.deleteConfirmBody")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							onClick={() => setConfirmDelete(false)}
						>
							{t("realtor.listings.detail.cancel")}
						</StudioButton>
						<Button
							variant="destructive"
							onClick={async () => {
								setConfirmDelete(false);
								const actedOn = listing.id;
								setActionsInFlight(withListing(actedOn));
								try {
									const result = await deleteListing(actedOn);
									if (!result.success) {
										reportFailure(
											result.error ??
												t("realtor.listings.detail.deleteFailFallback"),
											listing,
										);
										return;
									}
									// Moved on: leave the realtor where they are.
									if (!isStillShown(actedOn)) return;
									toast.success(t("realtor.listings.detail.deletedToast"));
									navigate("/realtor/listings");
								} catch (error) {
									console.error("Listing delete failed unexpectedly", error);
									reportFailure(
										t("realtor.listings.detail.deleteFailFallback"),
										listing,
									);
								} finally {
									setActionsInFlight(withoutListing(actedOn));
								}
							}}
						>
							{t("realtor.listings.detail.delete")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={confirmMarkSold} onOpenChange={setConfirmMarkSold}>
				<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<DialogHeader>
						<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
							{t("realtor.listings.detail.markSoldConfirmTitle")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.listings.detail.markSoldConfirmBody")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<StudioButton
							variant="secondary"
							onClick={() => setConfirmMarkSold(false)}
						>
							{t("realtor.listings.detail.cancel")}
						</StudioButton>
						<StudioButton
							onClick={() => {
								setConfirmMarkSold(false);
								runAction(
									() => markListingSold(listing.id),
									t("realtor.listings.detail.markedSoldToast"),
									t("realtor.listings.detail.markSoldFailFallback"),
								);
							}}
						>
							{t("realtor.listings.detail.markSold")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function KeyValue({
	label,
	value,
	isPrivate,
}: {
	label: string;
	value: string;
	isPrivate?: boolean;
}) {
	return (
		<div>
			<p className="text-xs font-mono uppercase tracking-wider text-dashboard-muted">
				{label}
				{isPrivate && (
					<span
						className="ml-1 text-[10px] uppercase"
						style={{ color: "var(--color-dashboard-pill-awaiting)" }}
					>
						(private)
					</span>
				)}
			</p>
			{/* The field is literally labelled "(private)" — masked in
			    session replay whenever it is. See lib/posthog.ts. */}
			<p
				data-sensitive={isPrivate || undefined}
				className="text-sm text-dashboard-text mt-1 font-body"
			>
				{value}
			</p>
		</div>
	);
}
